"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import {
	IconArrowRight,
	IconQuote,
	IconBook,
} from "@/lib/tabler-icons";
import { SectionHeading } from "@/components/ui/editorial/SectionHeading";
import { Caption } from "@/components/ui/editorial/Caption";
import { Kicker } from "@/components/ui/editorial/Kicker";
import { personalProjects } from "../data/projects";

const ProjectsSection = () => {
	return (
		<section className="p-4 xl:px-0 py-8" aria-label="Proyectos">
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="space-y-4"
				>
					<SectionHeading
						index="02"
						title={
							<>
								<em className="italic text-terracotta">Proyectos</em>
							</>
						}
						as="h2"
					/>
					<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
						Iniciativas que combinan literatura, educación y activismo para
						crear espacios de reflexión y transformación social.
					</p>
				</motion.header>

				{/* Projects Grid */}
				<div className="space-y-20">
					{personalProjects.map((project, index) => (
						<motion.div
							key={project.id}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: index * 0.1 }}
							viewport={{ once: true }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start text-center lg:text-left"
						>
							{/* Project Media Blocks */}
							<div className="relative order-2 lg:order-1 space-y-8">
								{/* Project Image Block */}
								<figure>
									<div className="rounded-xl overflow-hidden border border-border">
										<Image
											src={project.image}
											alt={`Imagen del proyecto: ${project.title}`}
											width={400}
											height={500}
											className="object-cover w-full h-auto"
										/>
									</div>
									<Caption>{project.title}</Caption>
								</figure>

								{/* Project Video Block */}
								{project.video && (
									<figure>
										<div className="rounded-xl overflow-hidden border border-border">
											<iframe
												src={project.video}
												title={`Video del proyecto: ${project.title}`}
												className="w-full h-64 sm:h-72"
												allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
												allowFullScreen
												loading="lazy"
											></iframe>
										</div>
										<Caption>Presentación en vídeo</Caption>
									</figure>
								)}
							</div>

							{/* Project Content */}
							<div className="space-y-8 order-1 lg:order-2">
								<div className="space-y-3">
									<Kicker>{project.subtitle}</Kicker>
									<h3 className="font-display font-semibold text-ink text-3xl md:text-4xl leading-tight">
										{project.title}
									</h3>
									<p className="font-body text-ink text-lg leading-relaxed">
										{project.description}
									</p>
								</div>

								{/* Features */}
								<div className="space-y-3">
									<h4 className="font-display font-semibold text-ink text-sm tracking-wide uppercase">
										Componentes del proyecto
									</h4>
									<ul className="space-y-2 w-fit mx-auto lg:mx-0">
										{project.features.map((feature, featureIndex) => (
											<li key={featureIndex} className="flex items-start gap-3">
												<span className="text-terracotta mt-1.5 shrink-0 text-xs">—</span>
												<span className="font-body text-ink text-base">{feature}</span>
											</li>
										))}
									</ul>
								</div>

								{/* Actions */}
								<div className="space-y-3">
									<h4 className="font-display font-semibold text-ink text-sm tracking-wide uppercase">
										Acciones específicas
									</h4>
									<div className="grid grid-cols-1 gap-2">
										{project.actions.map((action, actionIndex) => (
											<div
												key={actionIndex}
												className="p-4 border border-border rounded-lg space-y-1"
											>
												<h5 className="font-display font-semibold text-ink text-sm">
													{action.title}
												</h5>
												<p className="font-body text-muted-foreground text-sm">
													{action.description}
												</p>
											</div>
										))}
									</div>
								</div>

								{/* Quotes */}
								<div className="space-y-4">
									{project.quotes.map((quote, quoteIndex) => (
										<motion.blockquote
											key={quoteIndex}
											initial={{ opacity: 0, x: -20 }}
											whileInView={{ opacity: 1, x: 0 }}
											transition={{ duration: 0.5, delay: quoteIndex * 0.1 }}
											viewport={{ once: true }}
											className="border-l-2 border-terracotta pl-4 py-1"
										>
											<div className="flex items-start gap-2">
												<IconQuote className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
												<p className="font-body text-ink italic text-sm leading-relaxed">
													&quot;{quote}&quot;
												</p>
											</div>
										</motion.blockquote>
									))}
								</div>

								{/* CTA */}
								<motion.div
									initial={{ opacity: 0, y: 10 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5, delay: 0.2 }}
									viewport={{ once: true }}
								>
									<Link
										href={project.link}
										className="group inline-flex items-center gap-2 font-display text-sm text-ink border border-border px-5 py-3 rounded-lg hover:border-terracotta hover:text-terracotta transition-colors duration-200"
									>
										<IconBook className="w-4 h-4" />
										Descubre más sobre este proyecto
										<IconArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
									</Link>
								</motion.div>
							</div>
						</motion.div>
					))}
				</div>

				{/* Call to Action */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="mt-20 border-t border-border pt-12 space-y-6"
				>
					<div className="space-y-2">
						<Kicker>Proyectos Transformadores</Kicker>
						<h3 className="font-display font-semibold text-ink text-2xl md:text-3xl">
							¿Te interesa colaborar en algún proyecto?
						</h3>
						<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
							Cada proyecto está diseñado para crear impacto real en la
							sociedad. Si te identificas con alguno de estos temas, no dudes
							en contactarme.
						</p>
					</div>
					<div className="flex flex-col sm:flex-row gap-4">
						<Link
							href="/asociacion-equilibrio#contacto"
							className="inline-flex items-center justify-center gap-2 font-display text-sm bg-ink text-cream px-6 py-3 rounded-lg hover:bg-ink/90 transition-colors duration-200"
						>
							Contactar para colaborar
						</Link>
						<Link
							href="/asociacion-equilibrio"
							className="inline-flex items-center justify-center gap-2 font-display text-sm border border-border text-ink px-6 py-3 rounded-lg hover:border-terracotta hover:text-terracotta transition-colors duration-200"
						>
							Ver Asociación Equilibrio
						</Link>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default ProjectsSection;
